import { withObservables } from '@nozbe/watermelondb/react'
import { createContext, ReactNode, useContext, useMemo } from 'react'
import ObserveService from 'services/db/Observe'
import {
  AccountDoc,
  GroupDoc,
  MasterLabelDoc,
  ParticipantDataDoc,
  ProfileDoc,
  UserSettingsDoc
} from 'types/api/changes'
import { ICalendar } from 'types/google_events'
import { Model } from 'types/model'
import { getCachedConvertedCalendarsFromLocalStorage } from 'utils/third_party_calendars'

export interface EventGlobals {
  account?: Model<AccountDoc> | AccountDoc
  userSettings?: Model<UserSettingsDoc> | UserSettingsDoc
  profile?: Model<ProfileDoc>
  localParticipants?: Model<ParticipantDataDoc>[]
  masterLabel?: Model<MasterLabelDoc>
  topGroup?: Model<GroupDoc> | GroupDoc
  groupsById?: Map<string, Model<GroupDoc>>
  convertedCalendars: ICalendar[]
}

const EventGlobalsContext = createContext<EventGlobals | null>(null)

export function useEventGlobals(): EventGlobals | null {
  return useContext(EventGlobalsContext)
}

interface ProviderInputProps {
  account?: Model<AccountDoc> | AccountDoc
  userSettings?: Model<UserSettingsDoc> | UserSettingsDoc
  groups?: Model<GroupDoc>[] | GroupDoc[]
  children: ReactNode
}

interface ProviderInjectedProps extends ProviderInputProps {
  profile?: Model<ProfileDoc>
  localParticipants?: Model<ParticipantDataDoc>[]
  masterLabel?: Model<MasterLabelDoc>
  topGroup?: Model<GroupDoc>
}

function EventGlobalsProviderInner(props: ProviderInjectedProps) {
  const groupsById = useMemo(() => {
    const map = new Map<string, Model<GroupDoc>>()
    for (const g of (props.groups ?? []) as Model<GroupDoc>[]) {
      if (g?._id) map.set(g._id, g)
    }
    return map
  }, [props.groups])

  const convertedCalendars = useMemo(
    () => getCachedConvertedCalendarsFromLocalStorage(),
    [props.userSettings]
  )

  const value = useMemo<EventGlobals>(
    () => ({
      account: props.account,
      userSettings: props.userSettings,
      profile: props.profile,
      localParticipants: props.localParticipants,
      masterLabel: props.masterLabel,
      topGroup: props.topGroup,
      groupsById,
      convertedCalendars
    }),
    [
      props.account,
      props.userSettings,
      props.profile,
      props.localParticipants,
      props.masterLabel,
      props.topGroup,
      groupsById,
      convertedCalendars
    ]
  )

  return (
    <EventGlobalsContext.Provider value={value}>
      {props.children}
    </EventGlobalsContext.Provider>
  )
}

const EventGlobalsProvider = withObservables(
  ['userSettings'],
  (props: ProviderInputProps) => ({
    profile: ObserveService.Profile(),
    localParticipants: ObserveService.Participants(),
    masterLabel: ObserveService.MasterLabel(),
    topGroup: ObserveService.FirstGroup(props.userSettings as UserSettingsDoc)
  })
)(EventGlobalsProviderInner as any)

export default EventGlobalsProvider
